import { RUBRIQUES_TRAME, dateHeureDeTunis } from '@rh/shared';
import type { RefusEnvoi, SaisieLocale } from './types';

/**
 * Textes et formats communs aux écrans de la file d'envoi (docs/UI.md § Écran 3,
 * mode hors ligne). Les heures sont celles de Tunis, comme celles de l'API.
 */

export const MESSAGE_OUVERTURE_HORS_LIGNE =
  'Hors ligne : le numéro se compose, mais le panneau ne s’ouvre qu’avec du réseau. ' +
  'Notez l’échange et ouvrez le contact dès le retour du réseau.';
export const MESSAGE_ABANDON = 'Effacer définitivement cette saisie ? Elle n’a pas été reçue par le serveur.';
export const MESSAGE_COMPTE_DESACTIVE =
  'Compte désactivé : les saisies de ce compte ont été effacées de ce téléphone.';

/** « 10:42 », heure de Tunis. */
export function heureDeTunis(instant: number | string): string {
  return dateHeureDeTunis(instant).split(' à ')[1] ?? '';
}

/** « 22/09 », jour de Tunis. */
export function jourDeTunis(instant: number | string): string {
  return dateHeureDeTunis(instant).split(' à ')[0] ?? '';
}

/** « 1 saisie », « 2 saisies ». */
export function saisies(nombre: number): string {
  return `${nombre} saisie${nombre > 1 ? 's' : ''}`;
}

/** « 4,2 » */
export function noteEnFrancais(note: number): string {
  return note.toFixed(1).replace('.', ',');
}

/** « Appel du 15/09 à 10:02 », quand le nom du candidat n'est pas en mémoire. */
export function libelleContact(type: 'telephonique' | 'presentiel', debuteLe: string): string {
  return `${type === 'telephonique' ? 'Appel' : 'Entretien'} du ${dateHeureDeTunis(debuteLe)}`;
}

/**
 * Suite à donner à une saisie refusée (docs/UI.md § Écran 3, reprise d'une
 * saisie refusée) :
 * - `corriger` : formulaire modifiable, boutons habituels ;
 * - `bareme` : notation relue sur le nouveau barème, puis nouvelle validation ;
 * - `horodatage` : renvoi avec l'heure d'envoi ;
 * - `clos` : lecture seule, copie de la saisie, évaluation seule si possible ;
 * - `bloque` : lecture seule, copie ou abandon ;
 * - `efface` : contenu parti du téléphone, « J’ai compris ».
 */
export type ModeRefus = 'corriger' | 'bareme' | 'horodatage' | 'clos' | 'bloque' | 'efface';

const CODES_BLOQUANTS = new Set([
  'NON_AUTEUR',
  'ROLE_INSUFFISANT',
  'CANDIDAT_EMBAUCHE',
  'HORODATAGE_TROP_ANCIEN',
  'ENVOI_DEJA_UTILISE',
  'BAREME_INTROUVABLE',
]);

export function modeRefus(refus: Pick<RefusEnvoi, 'code' | 'contenuEfface'>): ModeRefus {
  if (refus.contenuEfface) return 'efface';
  switch (refus.code) {
    case 'BAREME_CHANGE':
      return 'bareme';
    case 'HORODATAGE_FUTUR':
    case 'HORODATAGE_AVANT_OUVERTURE':
      return 'horodatage';
    case 'CONTACT_CLOTURE':
    case 'EVALUATION_EXISTANTE':
      return 'clos';
    default:
      return refus.code !== null && CODES_BLOQUANTS.has(refus.code) ? 'bloque' : 'corriger';
  }
}

/** « Copier ma saisie » : observations et trame, pour les reporter dans un nouveau contact. */
export function texteSaisie(saisie: SaisieLocale): string {
  const lignes: string[] = [];
  if (saisie.type === 'presentiel') {
    for (const rubrique of RUBRIQUES_TRAME) {
      const reponse = saisie.reponses[rubrique.cle]?.trim();
      if (reponse) lignes.push(`${rubrique.libelle} : ${reponse}`);
    }
  }
  if (saisie.observations.trim()) lignes.push(`Observations : ${saisie.observations.trim()}`);
  return lignes.join('\n\n');
}
